/**
 * Tiny weather badge pinned to the bottom of the map. Shows the current
 * conditions at the trip's start location (or the user's GPS when no start
 * has been picked yet) with a friendly nudge — "bring a raincoat!", "wear
 * sunscreen!", that sort of thing.
 *
 * Tap the badge to open a fuller daily report (high/low, precipitation
 * chance, sunrise/sunset, wind) fetched lazily on first open.
 *
 * Uses Open-Meteo's free, key-less forecast API so we don't need to wire a
 * secret. Refetches whenever the lookup coordinate moves more than ~3 km.
 */
import { useEffect, useState } from "react";
import { Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSnow, Sun, Sunrise, Sunset, Wind, Droplets, Thermometer } from "lucide-react";
import type { LngLat } from "@/utils/polyline";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Props {
  /** Coordinate to forecast for. When undefined the badge hides itself. */
  coord: LngLat | undefined;
}

interface Weather {
  tempF: number;
  code: number;
  windMph: number;
  isDay: boolean;
}

interface DailyReport {
  highF: number;
  lowF: number;
  precipChance: number; // 0..100
  precipInches: number;
  windMaxMph: number;
  sunrise: string; // "7:14 AM"
  sunset: string;
  summary: string; // human label for code
}

/** Open-Meteo WMO weather codes → icon + cute one-liner. */
function describe(w: Weather): { Icon: typeof Cloud; quip: string } {
  const c = w.code;
  if (c === 0) return { Icon: w.isDay ? Sun : Cloud, quip: w.isDay ? "clear skies — sunglasses on!" : "clear and calm out there." };
  if (c <= 2) return { Icon: w.isDay ? Sun : Cloud, quip: "mostly sunny — perfect strolling weather." };
  if (c === 3) return { Icon: Cloud, quip: "overcast, but pleasant." };
  if (c === 45 || c === 48) return { Icon: CloudFog, quip: "foggy — drive gently." };
  if (c >= 51 && c <= 57) return { Icon: CloudDrizzle, quip: "light drizzle — grab a hood." };
  if (c >= 61 && c <= 67) return { Icon: CloudRain, quip: "bring a raincoat!" };
  if (c >= 71 && c <= 77) return { Icon: CloudSnow, quip: "snow — bundle up!" };
  if (c >= 80 && c <= 82) return { Icon: CloudRain, quip: "showers around — umbrella time." };
  if (c >= 85 && c <= 86) return { Icon: CloudSnow, quip: "snow showers — boots on!" };
  if (c >= 95) return { Icon: CloudLightning, quip: "thunderstorms — maybe stay in." };
  if (w.windMph >= 25) return { Icon: Wind, quip: "windy — hold onto your hat." };
  return { Icon: Cloud, quip: "looking decent out there." };
}

function summarizeCode(code: number): string {
  if (code === 0) return "Clear";
  if (code <= 2) return "Mostly sunny";
  if (code === 3) return "Overcast";
  if (code === 45 || code === 48) return "Fog";
  if (code >= 51 && code <= 57) return "Drizzle";
  if (code >= 61 && code <= 67) return "Rain";
  if (code >= 71 && code <= 77) return "Snow";
  if (code >= 80 && code <= 82) return "Showers";
  if (code >= 85 && code <= 86) return "Snow showers";
  if (code >= 95) return "Thunderstorms";
  return "Mixed";
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

/** Quantize a coord to ~3 km so tiny GPS jitter doesn't refetch. */
function key(c: LngLat): string {
  return `${c[0].toFixed(2)},${c[1].toFixed(2)}`;
}

/** In-memory + sessionStorage cache so the badge appears instantly on
 *  revisit / re-render. Open-Meteo data is fine to reuse for ~10 min. */
const TTL_MS = 10 * 60 * 1000;
type CacheEntry = { ts: number; current: Weather; daily: DailyReport };
const memCache = new Map<string, CacheEntry>();
function readCache(k: string): CacheEntry | null {
  const m = memCache.get(k);
  if (m && Date.now() - m.ts < TTL_MS) return m;
  try {
    const raw = sessionStorage.getItem(`wx:${k}`);
    if (!raw) return null;
    const e = JSON.parse(raw) as CacheEntry;
    if (Date.now() - e.ts > TTL_MS) return null;
    memCache.set(k, e);
    return e;
  } catch { return null; }
}
function writeCache(k: string, e: CacheEntry) {
  memCache.set(k, e);
  try { sessionStorage.setItem(`wx:${k}`, JSON.stringify(e)); } catch { /* ignore */ }
}

export function WeatherBadge({ coord }: Props) {
  const [data, setData] = useState<Weather | null>(null);
  const [open, setOpen] = useState(false);
  const [daily, setDaily] = useState<DailyReport | null>(null);

  // Single combined fetch (current + daily) so opening the popover is
  // instant. Hydrates synchronously from cache when available.
  useEffect(() => {
    if (!coord) {
      setData(null);
      setDaily(null);
      return;
    }
    const k = key(coord);
    const cached = readCache(k);
    if (cached) {
      setData(cached.current);
      setDaily(cached.daily);
      // Still fresh — skip network entirely.
      if (Date.now() - cached.ts < TTL_MS / 2) return;
    }
    let cancelled = false;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${coord[1]}&longitude=${coord[0]}&current=temperature_2m,weather_code,wind_speed_10m,is_day&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum,wind_speed_10m_max,weather_code,sunrise,sunset&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=1`;
    void fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => {
        if (cancelled) return;
        const cur = j.current;
        const d = j.daily;
        if (!cur || !d) return;
        const current: Weather = {
          tempF: Math.round(cur.temperature_2m),
          code: cur.weather_code,
          windMph: Math.round(cur.wind_speed_10m),
          isDay: cur.is_day === 1,
        };
        const dailyReport: DailyReport = {
          highF: Math.round(d.temperature_2m_max?.[0] ?? 0),
          lowF: Math.round(d.temperature_2m_min?.[0] ?? 0),
          precipChance: Math.round(d.precipitation_probability_max?.[0] ?? 0),
          precipInches: Number((d.precipitation_sum?.[0] ?? 0).toFixed(2)),
          windMaxMph: Math.round(d.wind_speed_10m_max?.[0] ?? 0),
          sunrise: fmtTime(d.sunrise?.[0] ?? ""),
          sunset: fmtTime(d.sunset?.[0] ?? ""),
          summary: summarizeCode(d.weather_code?.[0] ?? 0),
        };
        setData(current);
        setDaily(dailyReport);
        writeCache(k, { ts: Date.now(), current, daily: dailyReport });
      })
      .catch(() => {
        // Silent — weather is a nice-to-have, not load-bearing.
      });
    return () => { cancelled = true; };
  }, [coord ? key(coord) : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const dailyLoading = !daily && !!coord;

  if (!coord || !data) return null;
  const { Icon, quip } = describe(data);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="pointer-events-auto bg-card/90 backdrop-blur-md border border-border rounded-full shadow-elevated h-9 px-3 flex items-center gap-2 text-xs hover:bg-card transition-smooth"
          aria-label="View today's weather report"
        >
          <Icon className="w-4 h-4 text-pleasant flex-shrink-0" />
          <span className="font-semibold tabular-nums">{data.tempF}°F</span>
          {/* Narrower max-width on phones (40vw, was 60vw) so the pill stays
           *  compact and doesn't extend over the bottom-left Pleasant brand
           *  pill or shove the layout sideways on iPhone-class widths. */}
          <span className="text-muted-foreground truncate max-w-[40vw] sm:max-w-xs">{quip}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        avoidCollisions
        collisionPadding={{ top: 80, left: 8, right: 8 }}
        className="w-72 p-4"
      >
        <div className="flex items-center gap-3 mb-3">
          <Icon className="w-9 h-9 text-pleasant" />
          <div className="min-w-0">
            <div className="text-2xl font-bold tabular-nums leading-none">{data.tempF}°F</div>
            <div className="text-xs text-muted-foreground mt-1">
              {daily?.summary ?? summarizeCode(data.code)} · today
            </div>
          </div>
        </div>

        {dailyLoading && !daily ? (
          <div className="text-xs text-muted-foreground py-2">Loading today's report…</div>
        ) : daily ? (
          <div className="space-y-2 text-xs">
            <Row icon={Thermometer} label="High / Low">
              <span className="tabular-nums">{daily.highF}° / {daily.lowF}°F</span>
            </Row>
            <Row icon={Droplets} label="Rain chance">
              <span className="tabular-nums">
                {daily.precipChance}%
                {daily.precipInches > 0 && (
                  <span className="text-muted-foreground"> · {daily.precipInches}"</span>
                )}
              </span>
            </Row>
            <Row icon={Wind} label="Wind (max)">
              <span className="tabular-nums">{daily.windMaxMph} mph</span>
            </Row>
            <Row icon={Sunrise} label="Sunrise">
              <span className="tabular-nums">{daily.sunrise}</span>
            </Row>
            <Row icon={Sunset} label="Sunset">
              <span className="tabular-nums">{daily.sunset}</span>
            </Row>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground py-2">Couldn't load the daily report.</div>
        )}

        <p className="text-[11px] text-muted-foreground mt-3 italic leading-snug">{quip}</p>
      </PopoverContent>
    </Popover>
  );
}

function Row({ icon: Icon, label, children }: { icon: typeof Cloud; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
      <span className="font-medium">{children}</span>
    </div>
  );
}
